// src/contexts/AuthContext.js
import logger from '../utils/logger';
import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { onIdTokenChanged } from 'firebase/auth';
import { auth } from '../firebase';
import { useInactivityLogout } from '../hooks/useInactivityLogout';
import {
  setLogoutReason,
  LOGOUT_REASON_INACTIVITY,
  peekLogoutRedirectPath,
  setRequireCredentialReauth,
  broadcastInactivityLogout
} from '../utils/authSession';
import { isAllowedAuthEmail, isGuestEmail, isGuestUser } from '../utils/guestAuth';
import { shouldEnforceInactivityLogout, getInactivityTimeoutMs } from '../utils/kioskMode';
import { buildAuthContextUser } from '../utils/authContextUser';
import {
  syncMyClaimsFromProfile,
  redeemPendingInvitationIfExists,
  withTimeout,
  AUTH_BOOTSTRAP_TIMEOUT_MS
} from '../utils/authUtils';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Use ref to track if this is the initial load (avoids stale closure issue)
  const isInitialLoadRef = useRef(true);
  const previousAuthUidRef = useRef(null);
  // Use ref to prevent multiple simultaneous token refresh attempts
  const isRefreshingRef = useRef(false);
  const pendingTokenChangeRef = useRef(false);
  const latestUserRef = useRef(null);
  // Track recent permission errors to prevent false logouts
  const recentPermissionErrorsRef = useRef([]);
  const intentionalLogoutRef = useRef(false);
  // Inactivity warning state
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);
  const [inactivitySecondsRemaining, setInactivitySecondsRemaining] = useState(0);

  // Handle Google redirect result on mount (when user returns from OAuth redirect)
  // DISABLED: Let individual pages handle their own redirect results
  // useEffect(() => {
  //   const processRedirectResult = async () => {
  //     // Skip processing redirect results on registration page - let the registration page handle it
  //     if (window.location.pathname.includes('/complete-registration')) {
  //       logger.debug('AuthContext - Skipping redirect result processing on registration page');
  //       return;
  //     }

  //     try {
  //       const user = await handleGoogleRedirectResult();
  //       if (user) {
  //         logger.debug('AuthContext - Successfully processed Google redirect result:', user.uid);
  //       }
  //     } catch (error) {
  //       logger.error('AuthContext - Error processing Google redirect result:', {
  //         error: error.message,
  //         code: error.code
  //       });
  //       // Error is already handled in handleGoogleRedirectResult (user signed out if needed)
  //     }
  //   };

  //   processRedirectResult();
  // }, []); // Run only on mount

  useEffect(() => {
    // Listen for permission errors to prevent false logouts
    const handlePermissionError = (event) => {
      recentPermissionErrorsRef.current.push(event.detail.timestamp);
      // Keep only recent errors (last 10 seconds)
      const now = Date.now();
      recentPermissionErrorsRef.current = recentPermissionErrorsRef.current.filter(
        (timestamp) => now - timestamp < 10000
      );
    };

    window.addEventListener('permission-error', handlePermissionError);

    const handleTokenChange = async (user) => {
      if (!user && intentionalLogoutRef.current) {
        intentionalLogoutRef.current = false;
        isRefreshingRef.current = false;
        isInitialLoadRef.current = true;
        setCurrentUser(null);
        setLoading(false);
        return;
      }

      if (user) {
        if (intentionalLogoutRef.current) {
          isRefreshingRef.current = false;
          setCurrentUser(null);
          setLoading(false);
          return;
        }

        isRefreshingRef.current = true;
        const shouldForceRefresh = isInitialLoadRef.current;
        const isFreshSignIn = user.uid !== previousAuthUidRef.current;
        if (shouldForceRefresh || isFreshSignIn) {
          setLoading(true);
        }

        let tokenPreview = { claims: {} };
        try {
          // Validate email domain - company staff or guest accounts only
          tokenPreview = await withTimeout(
            user.getIdTokenResult(false),
            AUTH_BOOTSTRAP_TIMEOUT_MS,
            'getIdTokenResult'
          );
          if (user.email && isGuestEmail(user.email) && !isGuestUser(tokenPreview.claims)) {
            tokenPreview = await withTimeout(
              user.getIdTokenResult(true),
              AUTH_BOOTSTRAP_TIMEOUT_MS,
              'getIdTokenResult(force)'
            );
          }
          if (user.email && !isAllowedAuthEmail(user.email, tokenPreview.claims)) {
            logger.warn('AuthContext - Invalid email domain detected, signing out user:', {
              uid: user.uid,
              email: user.email
            });
            try {
              await auth.signOut();
            } catch (signOutError) {
              logger.warn('AuthContext - signOut after invalid domain failed:', signOutError?.message || signOutError);
            }
            setCurrentUser(null);
            return;
          }

          previousAuthUidRef.current = user.uid;
          isInitialLoadRef.current = false;

          // Show the app from the cached token immediately. Invitation redeem + claims
          // sync can take several seconds (cold Cloud Functions) and should not block login.
          const userObject = buildAuthContextUser(user, tokenPreview.claims || {});
          logger.debug('AuthContext - Setting currentUser:', { uid: user.uid, email: user.email, isInitialLoad: !isInitialLoadRef.current });
          setCurrentUser(userObject);
          setLoading(false);

          if ((shouldForceRefresh || isFreshSignIn) && !isGuestUser(tokenPreview.claims)) {
            try {
              const [redeemResult, syncResult] = await Promise.all([
                withTimeout(
                  redeemPendingInvitationIfExists(user),
                  AUTH_BOOTSTRAP_TIMEOUT_MS,
                  'redeemPendingInvitation'
                ),
                withTimeout(
                  syncMyClaimsFromProfile(user),
                  AUTH_BOOTSTRAP_TIMEOUT_MS,
                  'syncMyClaimsFromProfile'
                )
              ]);
              const needsTokenRefresh = Boolean(redeemResult?.redeemed || syncResult?.claimsChanged);
              if (needsTokenRefresh && latestUserRef.current?.uid === user.uid) {
                const token = await withTimeout(
                  user.getIdTokenResult(true),
                  AUTH_BOOTSTRAP_TIMEOUT_MS,
                  'getIdTokenResult(force)'
                );
                setCurrentUser(buildAuthContextUser(user, token.claims || {}));
              }
            } catch (bootstrapErr) {
              logger.warn('AuthContext - background claims bootstrap failed:', bootstrapErr?.message || bootstrapErr);
            }
          }
        } catch (error) {
          // Classify the error type
          const errorCode = error.code || error.name || 'UNKNOWN';
          const errorMessage = error.message || String(error);

          // Detect network/CORS errors (non-critical, should not log out)
          const isNetworkError = !errorCode.startsWith('auth/') && (
            errorMessage.includes('network') ||
          errorMessage.includes('NetworkError') ||
          errorMessage.includes('Failed to fetch') ||
          errorMessage.includes('CORS') ||
          errorMessage.includes('timeout') ||
          errorMessage.includes('ECONNREFUSED') ||
          errorMessage.includes('ENOTFOUND') ||
          errorCode === 'NETWORK_ERROR' ||
          errorCode === 'TIMEOUT' || (
              error.name === 'TypeError' && errorMessage.includes('fetch')));


          // Only these specific auth error codes should cause logout
          const isCriticalAuthError = errorCode === 'auth/user-token-expired' ||
          errorCode === 'auth/user-disabled' ||
          errorCode === 'auth/invalid-user-token' ||
          errorCode === 'auth/user-not-found' ||
          errorCode === 'auth/operation-not-allowed';

          logger.error('Error refreshing token:', {
            error: errorMessage,
            errorCode: errorCode,
            errorName: error.name,
            userId: user?.uid,
            isInitialLoad: isInitialLoadRef.current,
            isNetworkError: isNetworkError,
            isCriticalAuthError: isCriticalAuthError
          });

          // Only log out on truly critical auth errors that require re-authentication
          // NEVER log out on network errors, CORS errors, or temporary backend issues
          if (isCriticalAuthError && !isNetworkError) {
            logger.warn('AuthContext - Logging out due to critical auth error:', {
              errorCode: errorCode,
              userId: user?.uid,
              reason: 'Token validation failed - requires re-authentication'
            });
            setCurrentUser(null);
            // Clear Firebase session too — otherwise route gates spin forever on
            // auth.currentUser && !currentUser.
            try {
              await auth.signOut();
            } catch (signOutError) {
              logger.warn('AuthContext - signOut after critical auth error failed:', signOutError?.message || signOutError);
            }
          } else {
            // For network errors or non-critical errors, keep user logged in
            logger.warn('AuthContext - Token refresh error but keeping user logged in:', {
              errorCode: errorCode,
              errorType: isNetworkError ? 'network' : 'non-critical',
              userId: user?.uid,
              reason: isNetworkError ?
                'Network/CORS error - maintaining session with cached token' :
                'Non-critical error - maintaining session'
            });

            // Keep user logged in and try to use cached token
            try {
              if (errorMessage.includes('timeout')) {
                throw error;
              }
              // Try to get cached token result without forcing refresh
              const cachedToken = await withTimeout(
                user.getIdTokenResult(false),
                AUTH_BOOTSTRAP_TIMEOUT_MS,
                'getIdTokenResult(cached)'
              );
              setCurrentUser(buildAuthContextUser(user, cachedToken.claims || {}));
              logger.debug('AuthContext - Successfully used cached token to maintain session');
            } catch (fallbackError) {
              // If even cached token fails, use default values to maintain session
              // This is better than logging the user out due to temporary issues
              logger.warn('AuthContext - Could not get cached token, using defaults to maintain session:', {
                error: fallbackError.message,
                errorCode: fallbackError.code,
                userId: user?.uid,
                reason: 'Maintaining session despite token refresh failure - user can continue working'
              });
              setCurrentUser(buildAuthContextUser(user, tokenPreview.claims || {}));
            }
          }
        } finally {
          isRefreshingRef.current = false;
          setLoading(false);
        }
      } else {
        // Do NOT clear previousAuthUidRef here — a transient null during token refresh
        // would otherwise look like a fresh sign-in and set loading=true (remounting the app).
        if (intentionalLogoutRef.current) {
          previousAuthUidRef.current = null;
          intentionalLogoutRef.current = false;
          isRefreshingRef.current = false;
          isInitialLoadRef.current = true;
          setCurrentUser(null);
          setLoading(false);
          return;
        }

        // onIdTokenChanged fired with null user
        // Only sign out if we had a user before - don't sign out on initial load when there's no user
        const hadUser = currentUser !== null;

        // Verify user is actually signed out before logging out
        // Sometimes onIdTokenChanged fires with null due to temporary token issues or permission errors
        // Check if we've had recent permission errors - if so, don't log out
        const now = Date.now();
        const recentErrors = recentPermissionErrorsRef.current.filter(
          (timestamp) => now - timestamp < 30000 // Last 30 seconds (increased from 5)
        );
        recentPermissionErrorsRef.current = recentErrors;

        // Always check auth.currentUser first - it's the most reliable indicator
        // Wait a bit and check again - sometimes auth.currentUser is temporarily null
        await new Promise((resolve) => setTimeout(resolve, 100));
        const actualUser = auth.currentUser;

        // If we had recent permission errors, don't log out - it's likely a false positive
        // Also check if user is still authenticated
        if (recentErrors.length > 0 || actualUser) {
          if (recentErrors.length > 0) {
            logger.warn('AuthContext - onIdTokenChanged fired with null but recent permission errors detected, ignoring logout:', {
              recentErrorsCount: recentErrors.length,
              hasActualUser: !!actualUser,
              reason: 'Likely false logout due to permission error handling'
            });
          }
          // If user is still authenticated, restore their session
          if (actualUser) {
            // User is still authenticated, just had a token refresh issue
            // Try to maintain the session with the actual user
            logger.warn('AuthContext - onIdTokenChanged fired with null but user is still authenticated, maintaining session:', {
              userId: actualUser.uid,
              email: actualUser.email,
              reason: 'Token refresh issue - user still authenticated'
            });

            // Try to get token without forcing refresh
            try {
              const tokenResult = await withTimeout(
                actualUser.getIdTokenResult(false),
                AUTH_BOOTSTRAP_TIMEOUT_MS,
                'getIdTokenResult(recover)'
              );
              setCurrentUser(buildAuthContextUser(actualUser, tokenResult.claims || {}));
              logger.debug('AuthContext - Successfully maintained session after token refresh issue');
              setLoading(false);
              return;
            } catch (tokenError) {
              // If we can't get token, use defaults but keep user logged in
              logger.warn('AuthContext - Could not get token but maintaining session with defaults:', {
                error: tokenError.message,
                userId: actualUser.uid
              });
              setCurrentUser(buildAuthContextUser(actualUser, {}));
              setLoading(false);
              return;
            }
          } else if (recentErrors.length > 0) {
            if (intentionalLogoutRef.current || peekLogoutRedirectPath()) {
              intentionalLogoutRef.current = false;
              isRefreshingRef.current = false;
              isInitialLoadRef.current = true;
              setCurrentUser(null);
              setLoading(false);
              return;
            }
            // Had permission errors but no user - keep current state and don't log out
            logger.warn('AuthContext - onIdTokenChanged fired with null, recent permission errors detected, maintaining current user state:', {
              recentErrorsCount: recentErrors.length,
              currentUserId: currentUser?.uid
            });
            setLoading(false);
            return;
          }
        }

        // Only proceed with logout if we had a user before AND we have no recent permission errors AND no actual user
        // Don't log out on initial load when there was never a user
        if (hadUser && !actualUser) {
          // Double-check by waiting a bit more and checking again
          await new Promise((resolve) => setTimeout(resolve, 200));
          const doubleCheckUser = auth.currentUser;

          if (doubleCheckUser) {
            logger.warn('AuthContext - User reappeared after delay, maintaining session:', {
              userId: doubleCheckUser.uid,
              email: doubleCheckUser.email
            });
            // Try to restore the user
            try {
              const tokenResult = await withTimeout(
                doubleCheckUser.getIdTokenResult(false),
                AUTH_BOOTSTRAP_TIMEOUT_MS,
                'getIdTokenResult(double-check)'
              );
              setCurrentUser(buildAuthContextUser(doubleCheckUser, tokenResult.claims || {}));
            } catch (tokenError) {
              // Keep user logged in with defaults
              setCurrentUser(buildAuthContextUser(doubleCheckUser, {}));
            }
          } else {
            // Check again for recent permission errors before logging out
            const finalCheckNow = Date.now();
            const finalRecentErrors = recentPermissionErrorsRef.current.filter(
              (timestamp) => finalCheckNow - timestamp < 30000 // Last 30 seconds
            );

            if (finalRecentErrors.length > 0) {
              if (intentionalLogoutRef.current || peekLogoutRedirectPath()) {
                intentionalLogoutRef.current = false;
                isRefreshingRef.current = false;
                isInitialLoadRef.current = true;
                previousAuthUidRef.current = null;
                setCurrentUser(null);
                setLoading(false);
                return;
              }
              // Still have recent permission errors - don't log out
              logger.warn('AuthContext - User appears signed out but recent permission errors detected, maintaining current state:', {
                recentErrorsCount: finalRecentErrors.length,
                currentUserId: currentUser?.uid
              });
              setLoading(false);
              return;
            }

            // User is actually signed out - reset initial load flag for next login
            isInitialLoadRef.current = true;
            isRefreshingRef.current = false;
            previousAuthUidRef.current = null;
            logger.debug('AuthContext - User signed out (verified after delay)');
            setCurrentUser(null);
          }
        } else if (!hadUser) {
          // No user was logged in before, so this is just initial load - don't log anything about signing out
          logger.debug('AuthContext - No user on initial load');
          previousAuthUidRef.current = null;
          setCurrentUser(null);
        }
      }
      setLoading(false);
    };

    const drainTokenChanges = async () => {
      while (true) {
        if (isRefreshingRef.current) {
          pendingTokenChangeRef.current = true;
          return;
        }

        pendingTokenChangeRef.current = false;
        await handleTokenChange(latestUserRef.current);

        if (!pendingTokenChangeRef.current) {
          break;
        }
      }
    };

    let tokenChangeChain = Promise.resolve();

    const unsubscribe = onIdTokenChanged(auth, (user) => {
      latestUserRef.current = user;
      tokenChangeChain = tokenChangeChain
        .catch(() => {})
        .then(() => drainTokenChanges());
    });

    return () => {
      window.removeEventListener('permission-error', handlePermissionError);
      unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle inactivity warning - memoized to prevent unnecessary re-renders
  const handleInactivityWarning = useCallback((secondsRemaining) => {
    setInactivitySecondsRemaining(secondsRemaining);
    setShowInactivityWarning(true);
  }, []);

  const handleInactivityWarningDismiss = useCallback(() => {
    setShowInactivityWarning(false);
    setInactivitySecondsRemaining(0);
  }, []);

  const handleInactivityLogout = useCallback(() => {
    intentionalLogoutRef.current = true;
    setShowInactivityWarning(false);
    setInactivitySecondsRemaining(0);
    setLogoutReason(LOGOUT_REASON_INACTIVITY);
    setRequireCredentialReauth();
    broadcastInactivityLogout();
    setCurrentUser(null);
  }, []);

  const markIntentionalLogout = useCallback(() => {
    intentionalLogoutRef.current = true;
    setRequireCredentialReauth();
    setCurrentUser(null);
  }, []);

  // Use inactivity logout hook (all users, including iPad PWA and guest kiosk accounts)
  const inactivityLogoutEnabled = currentUser ? shouldEnforceInactivityLogout(currentUser) : false;
  const inactivityTimeoutMs = currentUser ? getInactivityTimeoutMs(currentUser) : undefined;

  const { extendSession } = useInactivityLogout(
    !!currentUser,
    handleInactivityLogout,
    handleInactivityWarning,
    handleInactivityWarningDismiss,
    {
      enabled: inactivityLogoutEnabled,
      timeoutMs: inactivityTimeoutMs
    }
  );

  const value = {
    currentUser,
    loading,
    showInactivityWarning,
    inactivitySecondsRemaining,
    extendSession,
    markIntentionalLogout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Export the useAuth hook
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
